import React from 'react';
import { Mail, Folder, Users, MessageSquare, CheckCircle, ShieldCheck, Crosshair, SquarePen, Brain, Share2 } from 'lucide-react';

// Mind-map reinterpretation of the hero diagram: Archen™ in the center with the source
// and output nodes floating around it, joined by curved dotted lines. Labels are fixed
// copy: do not change wording.

const SERIF = "'Cormorant Garamond', serif";

// x / y are percentages of a square canvas. Center node sits at 50,50.
const NODES = [
  { icon: Mail, label: 'Email', x: 15, y: 15 },
  { icon: Folder, label: 'Files', x: 48, y: 6 },
  { icon: Users, label: 'Meetings', x: 82, y: 13 },
  { icon: MessageSquare, label: 'Messages', x: 93, y: 30 },
  { icon: CheckCircle, label: 'Decisions', x: 9, y: 33 },
  { icon: ShieldCheck, label: 'Approvals', x: 15, y: 80 },
  { icon: Crosshair, label: 'Scope', x: 47, y: 93 },
  { icon: SquarePen, label: 'Commitments', x: 80, y: 85 },
  { icon: Brain, label: 'Project Memory', sub: 'Always up to date', x: 91, y: 66, emphasis: true },
];

const curve = (x, y) => {
  const mx = (50 + x) / 2;
  const my = (50 + y) / 2;
  const dx = x - 50;
  const dy = y - 50;
  const len = Math.hypot(dx, dy) || 1;
  const off = 7;
  return `M50,50 Q${(mx - (dy / len) * off).toFixed(2)},${(my + (dx / len) * off).toFixed(2)} ${x},${y}`;
};

const HeroDiagram = () => (
  <div className="relative w-full max-w-[560px] mx-auto aspect-square">
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
      {NODES.map((n) => (
        <path key={n.label} d={curve(n.x, n.y)} fill="none" stroke="#C6B79E" strokeWidth="0.35" strokeDasharray="1.1 1.4" strokeLinecap="round" />
      ))}
    </svg>

    {/* Center node */}
    <div className="absolute z-10" style={{ left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }}>
      <div className="flex items-center gap-3 rounded-2xl border border-[#E8DFD1] bg-white/95 shadow-[0_10px_30px_rgba(60,45,30,0.10)] pl-4 pr-3 py-3">
        <span className="text-[#1F1812] text-2xl leading-none" style={{ fontFamily: SERIF, fontWeight: 500 }}>Archen™</span>
        <span className="w-px h-6 bg-[#E5DDD0]" />
        <span className="text-[#2D2520] text-[13.5px] font-medium whitespace-nowrap">Reads. Connects. Understands.</span>
        <span className="ml-1 flex items-center justify-center w-8 h-8 rounded-lg bg-[#F1E8DA] shrink-0">
          <Share2 size={15} className="text-[#3A2F26]" strokeWidth={1.8} />
        </span>
      </div>
    </div>

    {/* Floating nodes */}
    {NODES.map((n) => {
      const Icon = n.icon;
      return (
        <div key={n.label} className="absolute z-10" style={{ left: `${n.x}%`, top: `${n.y}%`, transform: 'translate(-50%,-50%)' }}>
          <div className={`flex flex-col rounded-xl border px-3 py-2 whitespace-nowrap transition-all duration-300 hover:-translate-y-0.5 ${n.emphasis ? 'bg-[#F1E8DA] border-[#E0D2BC]' : 'bg-white/90 border-[#EAE1D3]'} shadow-[0_2px_12px_rgba(60,45,30,0.06)]`}>
            <div className="flex items-center gap-1.5">
              <Icon size={14} className="text-[#3A2F26]" strokeWidth={1.7} />
              <span className="text-[#2D2520] text-[13px] font-medium">{n.label}</span>
            </div>
            {n.sub && <span className="text-[#9C8A73] text-[10.5px] mt-0.5 pl-[22px]">{n.sub}</span>}
          </div>
        </div>
      );
    })}
  </div>
);

export default HeroDiagram;
